import { datadogLogs } from '@datadog/browser-logs';
import type { LogsEvent } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent } from '@datadog/browser-rum';
import {
  getSessionReplaySampleRate,
  isSessionReplayDisabled,
} from './Datadog/sessionReplayDomainBasedConfig';

declare global {
  interface Window {
    DATADOG_RUM_APPLICATION_ID: string;
    DATADOG_RUM_CLIENT_TOKEN: string;
    DATADOG_RUM_ENABLED: boolean;
    DATADOG_RUM_SERVICE?: string;
    APP_VERSION?: string;
  }
}

// Match Userflow's Zendesk probe by message text + Userflow stack marker — the
// "Userflow.js: Error when configuring ZENDESK" prefix is browser-only. DEV-21423.
const USERFLOW_ZENDESK_ERROR_MESSAGE = 'Zendesk is not installed';
const USERFLOW_STACK_MARKER = 'js.userflow.com';

export const isUserflowZendeskProbeError = (
  message: string | undefined,
  stack: string | undefined
): boolean =>
  (message?.includes(USERFLOW_ZENDESK_ERROR_MESSAGE) ?? false) &&
  (stack?.includes(USERFLOW_STACK_MARKER) ?? false);

export const dropUserflowZendeskLog = (event: LogsEvent): boolean => {
  const combinedMessage = [event.message, event.error?.message]
    .filter(Boolean)
    .join(' ');
  return !isUserflowZendeskProbeError(combinedMessage, event.error?.stack);
};

export const dropUserflowZendeskRumEvent = (event: RumEvent): boolean => {
  if (event.type !== 'error') {
    return true;
  }
  return !isUserflowZendeskProbeError(event.error.message, event.error.stack);
};

/**
 * Initialize both Datadog RUM and Logs using shared configuration
 */

if (window.DATADOG_RUM_ENABLED) {
  const serviceName = window.DATADOG_RUM_SERVICE ?? 'checkbox-app-dev';
  const sessionReplaySampleRate = getSessionReplaySampleRate();
  const shouldDisableSessionReplay = isSessionReplayDisabled();

  // Initialize RUM
  // session replay rate based on domain whitelist
  datadogRum.init({
    applicationId: window.DATADOG_RUM_APPLICATION_ID,
    clientToken: window.DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: serviceName,
    env: window.ENV,
    // this is being set in production.Dockerfile
    version: window.APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    allowedTracingUrls: [
      { match: /\/api\//, propagatorTypes: ['tracecontext'] },
    ],
    traceSampleRate: 100,
    beforeSend: dropUserflowZendeskRumEvent,
    // TODO: DEV-15620 add react plugin once react is upgraded to v18
    // plugins: [reactPlugin({ router: true })],
  });

  // Initialize Logs with the same token and configuration
  datadogLogs.init({
    clientToken: window.DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: serviceName,
    env: window.ENV,
    version: window.APP_VERSION,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: dropUserflowZendeskLog,
  });

  if (shouldDisableSessionReplay) {
    datadogLogs.logger.info(
      `Datadog RUM and Logs initialized successfully. Session replay disabled for domain: ${window.location.hostname}`
    );
  } else {
    datadogLogs.logger.info('Datadog RUM and Logs initialized successfully');
  }
}
